import { modeDuration } from "@/lib/interviewModes";
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useInvite } from "@/hooks/useInvite";
import { CandidateFlow } from "@/shell/CandidateFlow";
import { Button, Spinner } from "@/components/primitives";
import { InviteError } from "./InviteError";

// C5 — Session reveal. Shows what the personalisation quiz answers unlocked,
// including a preparedness score plan. Answers read from sessionStorage.

interface PersonaliseData {
  concern: string;
  practiceStyle: string;
  prepLevel: string;
  timeUntilInterview: string;
}

const CONCERN_LABELS: Record<string, string> = {
  narrative: "Career story and narrative clarity",
  tough: "Handling curveball and pressure questions",
  seniority: "Projecting the right level of seniority",
  closing: "Closing the conversation decisively",
};

const CONCERN_DRILL: Record<string, string> = {
  narrative: "TMAY narrative drill + PCAR structure",
  tough: "Two curveball questions calibrated to this role",
  seniority: "Exec-level framing check after each answer",
  closing: "Outro pitch and closing technique drill",
};

const TIME_SESSIONS: Record<string, { s1: string; s2: string; s3: string; note: string }> = {
  "48h": {
    s1: "Today (your first full mock — 30 minutes)",
    s2: "Tomorrow (second full mock with adjusted difficulty)",
    s3: "Day of interview: drills only, 10 minutes max",
    note: "You have limited time — prioritise completing both sessions rather than over-drilling.",
  },
  week: {
    s1: "Today (your first full mock — 30 minutes)",
    s2: "In 3 days (second mock — harder questions)",
    s3: "2 days before your interview (final mock)",
    note: "Three sessions spaced over a week significantly outperforms cramming the day before.",
  },
  twoweeks: {
    s1: "Today (your first full mock — 30 minutes)",
    s2: "In 5 days (second mock — adjusted difficulty)",
    s3: "3 days before your interview (final sharpening session)",
    note: "Spaced practice over two weeks is the highest-impact preparation pattern.",
  },
  more: {
    s1: "This week (your first full mock — 30 minutes)",
    s2: "Next week (second mock — harder questions)",
    s3: "3 days before your interview (final mock)",
    note: "With more than two weeks, aim for 10 drills spread across the period alongside your sessions.",
  },
};

function calcScore(answers: PersonaliseData | null): number {
  if (!answers) return 20;
  let score = 20;
  if (answers.prepLevel === "reading") score += 10;
  if (answers.prepLevel === "practised") score += 20;
  return Math.min(score, 40);
}

function ScoreBar({ value, max = 100 }: { value: number; max?: number }) {
  const pct = Math.round((value / max) * 100);
  return (
    <div className="relative h-3 rounded-full bg-slate-200 overflow-hidden">
      <div
        className="absolute inset-y-0 left-0 rounded-full bg-accent transition-all duration-700"
        style={{ width: `${pct}%` }}
      />
      {[30, 60, 80].map((m) => (
        <div
          key={m}
          className="absolute inset-y-0 w-px bg-cream"
          style={{ left: `${m}%` }}
        />
      ))}
    </div>
  );
}

export function SessionReveal() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { data, error, loading } = useInvite(token);
  const [answers, setAnswers] = useState<PersonaliseData | null>(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("ic_personalise");
      if (raw) setAnswers(JSON.parse(raw) as PersonaliseData);
    } catch {
      // ignore
    }
  }, []);

  if (loading) {
    return (
      <CandidateFlow>
        <div className="flex justify-center py-10"><Spinner size={28} /></div>
      </CandidateFlow>
    );
  }
  if (error || !data) return <InviteError message={error ?? "Invite not found."} />;

  const score = calcScore(answers);
  const concern = answers?.concern ?? "narrative";
  const timeKey = answers?.timeUntilInterview ?? "week";
  const plan = TIME_SESSIONS[timeKey] ?? TIME_SESSIONS.week;
  const sessionPlan = answers?.practiceStyle === "free"
    ? "Practise freely, then review at the end"
    : answers?.practiceStyle === "both"
    ? "Structured feedback after each answer, with a summary review"
    : "Structured feedback after each answer";

  const agency = data.agencyName || "your consultant";

  return (
    <CandidateFlow
      agencyName={data.agencyName}
      logoUrl={data.branding?.logoUrlLight || undefined}
      logoFilter={data.branding?.logoFilter}
      width="wide"
    >
      <Link to={`/invite/${token}/personalise`} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-ink">
        <span aria-hidden="true">←</span> Back
      </Link>

      <div className="mb-5">
        <p className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-accent">
          Your session is ready
        </p>
        <h1 className="text-xl font-semibold text-ink">Based on your answers</h1>
      </div>

      {/* Preparedness score */}
      <div className="mb-5 rounded-lg border border-hair bg-cream p-5 shadow-sm">
        <p className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          Your preparedness score
        </p>
        <div className="mb-3 flex items-baseline gap-2">
          <span className="text-3xl font-bold text-ink">{score}%</span>
          <span className="text-sm text-slate-500">of 100% interview-ready</span>
        </div>
        <ScoreBar value={score} />
        <div className="mt-2 flex justify-between font-mono text-[9px] text-slate-400">
          <span>Starting</span>
          <span>30% — First session</span>
          <span>60% — Three sessions</span>
          <span>80%+</span>
        </div>
        <p className="mt-3 text-xs leading-relaxed text-slate-500">
          Each session adds ~15–20 points. Candidates who complete three sessions average 78%.
          Your full scorecard will be shared with {agency}.
        </p>
      </div>

      {/* Session config */}
      <div className="mb-5 rounded-lg border border-hair bg-cream p-5 shadow-sm">
        <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 pb-2 border-b border-hair">
          Your session is configured for
        </p>
        <div className="space-y-3">
          {[
            ["Focus area", CONCERN_LABELS[concern] ?? concern],
            ["Drill type", CONCERN_DRILL[concern] ?? "Full practice interview"],
            ["Feedback style", sessionPlan],
            ["Estimated time", modeDuration(data.mode) ?? "Varies by session"],
          ].map(([k, v]) => (
            <div key={k} className="flex items-start justify-between gap-4 text-sm">
              <span className="font-mono text-xs text-slate-500 shrink-0">{k}</span>
              <span className="font-medium text-ink text-right">{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Plan */}
      <div className="mb-6 rounded-lg border border-hair bg-cream p-5 shadow-sm">
        <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 pb-2 border-b border-hair">
          Your preparation plan
        </p>
        <div className="space-y-3">
          {[
            ["Session 1", plan.s1],
            ["Session 2", plan.s2],
            ["Session 3", plan.s3],
          ].map(([k, v]) => (
            <div key={k} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-50 font-mono text-[9px] font-semibold text-accent">
                {k.slice(-1)}
              </span>
              <div className="text-sm leading-snug">
                <span className="font-semibold text-ink">{k}: </span>
                <span className="text-slate-500">{v}</span>
              </div>
            </div>
          ))}
        </div>
        {plan.note && (
          <p className="mt-3 rounded-md bg-paper-2 px-3 py-2 text-xs leading-relaxed text-slate-500">
            ✦ {plan.note}
          </p>
        )}
      </div>

      <Button variant="accent" className="w-full" onClick={() => navigate(`/invite/${token}/setup`)}>
        Upload my CV and job description to personalise further →
      </Button>
      <button
        type="button"
        onClick={() => navigate(`/invite/${token}/setup`)}
        className="mt-3 w-full text-center text-sm text-slate-400 hover:text-slate-600"
      >
        Skip upload and start now
      </button>
    </CandidateFlow>
  );
}
